import { DOCUMENT_PRIVILEGE } from '@tencent/ark-domain';
import UserAgent from '@tencent/docs-user-agent';
import { IExtension } from '@tencent/ark-extensions';
import { CalculatedStrategyToggle } from '@tencent/docs-scenario-component-docs-toggle-client';
import debounce from 'lodash/debounce';

import { IBaseCopyPaste } from '@src/slide/application/service/copy-paste/core/base/base-copy-paste.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import ArkLoader from '@src/js/common/ark-extensions';
import componentConfig from './component-config';
import { IScreenshotManager } from './screenshot.interface';
import { getToggleByType, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';
import type ExportImageManager from '../export-image-manager';

const needScreenshotPrivileges = [DOCUMENT_PRIVILEGE.allOnlyRead, DOCUMENT_PRIVILEGE.allOnlyWrite];

export default class ScreenshotManager implements IScreenshotManager {
    public uiDataService: IUiDataService;
    public copyPasteManager: IBaseCopyPaste;
    public exportImageManager: ExportImageManager;
    private screenshotComponent: IExtension;
    private componentShow = false;
    private screenshotStrategy = {};

    public setCopyPasteManager(copyPasteManager: IBaseCopyPaste) {
        this.copyPasteManager = copyPasteManager;
    }

    public setExportImageManager(exportImageManager: ExportImageManager) {
        this.exportImageManager = exportImageManager;
    }

    public setUiDataService(uiDataService: IUiDataService) {
        this.uiDataService = uiDataService;
    }

    public async init() {
        const {
            status: enableScreenshot,
            strategyGroupDataset = {},
        } = getToggleByType<CalculatedStrategyToggle>(ToggleType.SCREENSHOT_COMPONENT);
        if (!enableScreenshot) {
            return;
        }
        this.screenshotStrategy = strategyGroupDataset;
        // 监听文档权限变化
        this.listenDocumentPrivilegeChange();
        // 只有公开文档才需要截图
        const {
            documentPrivilege,
        } = ArkLoader.extensionService.dependentServiceCombinder.authDomain.authAggregate.authInfoEntity;
        if (needScreenshotPrivileges.indexOf(documentPrivilege) === -1) {
            return;
        }
        this.showScreenshotComponent();
    }

    private async showScreenshotComponent(forceCapture = false) {
        if (this.componentShow) {
            return;
        }
        const component = await this.getScreenshotComponent();
        component.show({
            forceCapture,
            triggerStrategy: this.screenshotStrategy,
            onFetchPage: async (pageNum: number, resolution: number) => {
                let showingSlides: ISlideModel[] = this.uiDataService.presentation.children.filter(s => s.isShowInPlay);
                if (showingSlides.length === 0) {
                    // 全部隐藏，默认视为全部不隐藏
                    showingSlides = this.uiDataService.presentation.children;
                }
                const slide = showingSlides[pageNum - 1];
                // TODO: 设置 isImageCopying，强制触发加载生成图片模块，后续需要调换成新接口
                this.exportImageManager.isImageCopying = true;
                const canvas = await this.copyPasteManager.getSlideDataToCanvas([slide], resolution);
                this.exportImageManager.isImageCopying = false;
                return canvas;
            },
        });
        this.componentShow = true;
    }

    private async disposeScreenshotComponent() {
        if (!this.componentShow) {
            return;
        }
        const component = await this.getScreenshotComponent();
        component.dispose();
        this.componentShow = false;
    }

    private listenDocumentPrivilegeChange() {
        // 加上防抖，避免权限频繁变化
        // eslint-disable-next-line max-len
        ArkLoader.extensionService.dependentServiceCombinder.authDomain.authAggregate.authInfoEntity.onAuthChanged(debounce(({
            newDocumentPrivilege,
        }) => {
            if (needScreenshotPrivileges.indexOf(newDocumentPrivilege) === -1) {
                this.disposeScreenshotComponent();
                return;
            }
            this.showScreenshotComponent(true);
        }, 2000));
    }

    private async getScreenshotComponent(): Promise<IExtension> {
        if (!this.screenshotComponent) {
            await ArkLoader.extensionService.scanAndHandleExtensions([componentConfig]);
            this.screenshotComponent = await ArkLoader.extensionService.getExtension(componentConfig.name);
        }
        return this.screenshotComponent;
    }
}
